import React, { Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import ProductTilesListContext from '@stone/scripts/contexts/ProductTilesListContext';
import analytics from '@stone/utils/analytics';
import Button from '@stone/ui/button/Button';
import einsteinutils from '@utils/einsteinutils';
import { getColorVariations, getProductMasterId, getSizeVariations } from '@utils/product';
import { handleTileNavigation } from '@utils/useMousePosition';
import DwMarker from '@common/components/common/DwMarker';
import api from '@common/utils/api';
import useMediaQueries from '@stone/hooks/useMediaQueries';
import ProductLink from '@stone/components/common/ProductLink';
import ErrorModal from '@components/error/ErrorModal';
import ProductWishlistToggle from '@stone/components/product/ProductWishlistToggle';
import ProductSelectionColor from '@stone/components/product/ProductSelectionColor';
import ProductSelectionSizesExpanded from '@stone/components/product/ProductSelectionSizesExpanded';
import ProductAddToBag from '@stone/components/product/ProductActionAddToBag';
import ProductActionNotifyMe from '@stone/components/product/ProductActionNotifyMe';
import ProductPrice from '@stone/components/product/ProductPrice';
import Icon from '@stone/ui/icon/Icon';
import ProductTileVisual from '@stone/components/product/ProductTileVisual';
import ProductLabels from './ProductLabels';
const LoginModal = React.lazy(() => import('@stone/components/login/LoginModal'));
const WishlistActionNotifyMe = React.lazy(() => import('@stone/components/wishlist/WishlistActionNotifyMe'));
const WishlistMoveToCart = React.lazy(() => import('@stone/components/wishlist/WishlistMoveToCart'));
const WishlistRemoveItem = React.lazy(() => import('@stone/components/wishlist/WishlistRemoveItem'));
const ProductBoutiqueModal = React.lazy(() => import('@stone/components/reserve/ProductBoutiqueModal'));
const ProductTile = (props) => {
    const productObject = typeof props.product === 'string' ? JSON.parse(props.product) : props.product;
    /* REFERENCES */
    const tileRef = useRef(null);
    const tileForm = useRef(null);
    const isChanged = useRef(false);
    /* HOOKS */
    const { status, navigation } = handleTileNavigation(tileRef);
    const { t } = useTranslation();
    const analyticsContext = useContext(ProductTilesListContext);
    const isReducedMotion = useReducedMotion();
    const { isDesktop, isWideDesktop } = useMediaQueries();
    /* STATES */
    const [sizeVariations, setSizeVariations] = useState();
    const [currentSize, setCurrentSize] = useState();
    const [currentColor, setCurrentColor] = useState();
    const [initialColor, setInitialColor] = useState();
    const [originalUuid, setOriginalUuid] = useState(null);
    const [forceOpenSizeSelector, setForceOpenSizeSelector] = useState(false);
    const [mustselectSize, setMustselectSize] = useState(false);
    const [showAddToBagMessage, setShowAddToBagMessage] = useState(false);
    const [variantPrice, setVariantPrice] = useState();
    const [errorMessage, setErrorMessage] = useState();
    const [errorTitle, setErrorTitle] = useState();
    const [loginOpen, setLoginOpen] = useState(false);
    const [product, setProduct] = useState(productObject);
    const [tileState, setTileState] = useState((props.fullExpanded && 'EXPANDED') || 'QUIET');
    const [isOver, setIsOver] = useState(false);
    const [modalsOpen, setModalsOpen] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    /* MEMO */
    const buttonStyle = useMemo(() => {
        return props.inShopTheLookPanel ? 'primary' : 'tertiary';
    }, [props.inShopTheLookPanel]);
    const colourVariations = useMemo(() => {
        return getColorVariations(product).map((value) => {
            return Object.assign(Object.assign({}, value), { groupPid: product.masterId + value.id });
        });
    }, [product]);
    const isComingSoon = useMemo(() => {
        return (product === null || product === void 0 ? void 0 : product.isComingSoon) === 2;
    }, [product]);
    const isExpanded = useMemo(() => {
        return isWideDesktop || props.fullExpanded || props.inShopTheLookModule || props.inIconGallery;
    }, [isWideDesktop, props.fullExpanded, props.inShopTheLookModule, props.inIconGallery]);
    const showQuickShop = useMemo(() => {
        return isExpanded && !props.disableQuickShop && !window.unshoppable && props.inIconGallery && isWideDesktop;
    }, [isWideDesktop, product, props.disableQuickShop, props.fullExpanded, props.inIconGallery]);
    const notMember = useMemo(() => {
        return !window.loggedin && (product === null || product === void 0 ? void 0 : product.unlock);
    }, [product]);
    const useColorCarousel = useMemo(() => {
        if (isDesktop) {
            return colourVariations.length > 6;
        }
        if (isExpanded) {
            return colourVariations.length > 5;
        }
        return colourVariations.length > 3;
    }, [isDesktop, isWideDesktop, colourVariations]);
    const showWishlistButton = useMemo(() => {
        return props.wishlist || (!!isWideDesktop && tileState !== 'QUIET');
    }, [props.wishlist, isWideDesktop, tileState]);
    const hideCta = useMemo(() => {
        return props.type === 'RESERVE';
    }, [props.type]);
    const analyticsAddToBagDetail = useMemo(() => {
        if (props.inShopTheLookPanel === 'fromModule' || props.inShopTheLookModule) {
            return 'shop the look main';
        }
        else if (props.inShopTheLookPanel === 'fromInfo') {
            return 'shop the look info panel';
        }
        else {
            return 'quick shop';
        }
    }, [props.inShopTheLookPanel, props.inShopTheLookModule]);
    /* RENDER HOOKS */
    useEffect(() => {
        setProduct(productObject);
        const colorVariations = getColorVariations(productObject);
        const selectedColor = colorVariations.find((v) => v.selected) || colorVariations[0];
        setCurrentColor(selectedColor);
        if (!initialColor)
            setInitialColor(selectedColor);
        if (props.fullExpanded) {
            const sizeVariations = getSizeVariations(productObject);
            const selectedSize = sizeVariations.find((v) => v.selected);
            selectedSize ? setCurrentSize(selectedSize) : setCurrentSize(undefined);
        }
    }, [props.product]);
    useEffect(() => {
        if (!originalUuid && productObject) {
            setOriginalUuid(productObject.uuid);
        }
    }, [productObject, originalUuid]);
    useEffect(() => {
        if (!(product === null || product === void 0 ? void 0 : product.id)) {
            return;
        }
        if (product.isobject) {
            tileRef.current.insertAdjacentHTML('afterbegin', product.isobject);
        }
        einsteinutils.trackProductInCategory(product.id, props.category, props.sortingrule);
    }, [product]);
    useEffect(() => {
        !props.fullExpanded && setTileState((isWideDesktop && !props.inIconGallery) || props.inShopTheLookModule ? 'QUIET' : 'LIVELY');
    }, [isWideDesktop, props.fullExpanded, props.inShopTheLookModule]);
    useEffect(() => {
        if (!modalsOpen && !props.fullExpanded && ((!props.inShopTheLookModule && isDesktop) || isWideDesktop)) {
            if (navigation === 'mouse') {
                if (status === 'IN') {
                    setIsOver(true);
                    if (tileState === 'QUIET') {
                        setTileState('LIVELY');
                    }
                }
                else {
                    setIsOver(false);
                    //on hover change reset isChanged
                    isChanged.current = false;
                    if (isWideDesktop && tileState != 'QUIET' && !props.inIconGallery) {
                        setTileState('QUIET');
                    }
                    else if (!isWideDesktop && tileState != 'LIVELY') {
                        setTileState('LIVELY');
                    }
                }
            }
        }
    }, [status, navigation, props.fullExpanded, modalsOpen, isDesktop]);
    useEffect(() => {
        let sizeVariations = getSizeVariations(product);
        // se è taglia unica la imposto
        if (sizeVariations.length == 1) {
            setCurrentSize(sizeVariations[0]);
        }
        else if (currentSize) {
            // check se esiste la taglia selezionata e riseleziono per aggiornare lo stock
            if (sizeVariations.find((el) => el.value == currentSize.value)) {
                const sizeUpdated = sizeVariations.find((el) => el.value == currentSize.value);
                setCurrentSize(sizeUpdated);
            }
            else {
                setCurrentSize(undefined);
            }
        }
        setSizeVariations(sizeVariations);
    }, [product]);
    useEffect(() => {
        if (tileState !== 'EXPANDED' && mustselectSize)
            setMustselectSize(false);
    }, [tileState]);
    /* METHODS */
    const sendAnalytics = (context) => {
        analytics.addToBag(product, currentSize, variantPrice, { detail: context });
    };
    const changeSize = (variation) => {
        var _a, _b;
        if (props.wishlist) {
            let _wishlistProduct = props.product;
            let itsok = props.updateWishlist('update', {
                oldProductId: _wishlistProduct.pid,
                newProductId: _wishlistProduct.id + variation.id,
                recipeId: _wishlistProduct.mbm ? (_a = _wishlistProduct.mbm) === null || _a === void 0 ? void 0 : _a.recipeId : '',
            });
            if (!itsok) {
                setErrorMessage(t('wishlist.itemalreadypresent'));
                return;
            }
        }
        setCurrentSize(variation);
        setMustselectSize(false);
        let hookresult = !props.onChangeSize || props.onChangeSize(variation);
        //block the api if hook result is FALSE
        if (hookresult !== false) {
            // qui non va cambiato il prodotto, va solo salvata la taglia selezionata che serve per l'add to cart
            if (((_b = product === null || product === void 0 ? void 0 : product.price) === null || _b === void 0 ? void 0 : _b.type) === 'range') {
                // prezzo diverso per taglia
                api.variantPrice(`${product.masterId}${currentColor.id}${variation.id}`).then((data) => setVariantPrice(data));
            }
        }
    };
    const onSuccessAddToBag = () => {
        props.onSuccessAddToBag && props.onSuccessAddToBag();
        setShowAddToBagMessage(true);
    };
    const changeColor = (colorVariation) => {
        var _a;
        if (!colorVariation || product.id.endsWith(colorVariation.id)) {
            return;
        }
        setCurrentColor(colorVariation);
        let baseId = getProductMasterId(product);
        let newId = baseId + colorVariation.id;
        let hookresult = !props.onChangeColor || props.onChangeColor(colorVariation);
        if (props.wishlist) {
            let _wishlistProduct = props.product;
            let result = props.updateWishlist('update', {
                oldProductId: _wishlistProduct.pid,
                newProductId: newId,
                recipeId: _wishlistProduct.mbm ? (_a = _wishlistProduct.mbm) === null || _a === void 0 ? void 0 : _a.recipeId : '',
            });
            if (!result) {
                setErrorMessage(t('wishlist.itemalreadypresent'));
                setCurrentColor(currentColor);
            }
            else {
                isChanged.current = isOver;
            }
        }
        else if (hookresult === false) {
            //block the api if hook result is FALSE
        }
        else {
            api.getProductTile(newId, initialColor === null || initialColor === void 0 ? void 0 : initialColor.id).then((data) => {
                isChanged.current = isOver;
                if (analyticsContext === null || analyticsContext === void 0 ? void 0 : analyticsContext.analyticsData)
                    analytics.productImpressionsChange(Object.assign(Object.assign({}, analyticsContext.analyticsData), { product: data, index: props.index }));
                setProduct(data);
            });
        }
    };
    const fakeClick = () => {
        var _a;
        const link = (_a = tileRef === null || tileRef === void 0 ? void 0 : tileRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.product-tile__link');
        if (link) {
            link.click();
        }
    };
    /* CALLBACKS */
    const quickBuyError = useCallback(() => {
        if (showAddToBagMessage) {
            return (React.createElement("span", { className: "visually-hidden", role: "alert" }, t('bag.justadded')));
        }
        if (mustselectSize) {
            return (React.createElement("span", { className: "alert", role: "alert" },
                React.createElement(Icon, { name: "alert", family: '10', ariaHidden: true }),
                React.createElement("span", { "aria-hidden": true }, t('label.selectasize'))));
        }
    }, [showAddToBagMessage, mustselectSize]);
    const tilePrice = useCallback(() => {
        return (!props.hideprice && (React.createElement("span", { className: "product-tile__price" },
            React.createElement(ProductPrice, { price: variantPrice || (product === null || product === void 0 ? void 0 : product.price), promotions: product.promotions, inline: true, isPlp: props.type === 'PLP' }))));
    }, [props.hideprice, variantPrice, product]);
    const productSEOName = useCallback(() => {
        return (product === null || product === void 0 ? void 0 : product.seoName) && React.createElement("p", { className: "product-tile__seoname" }, product.seoName);
    }, [product]);
    const quickBuyButton = useCallback(() => {
        // se la taglia non è selezionata
        if (!currentSize) {
            let ctaData = buttonData();
            return (React.createElement(Button, { type: "button", cls: [`button--${buttonStyle}`], "aria-busy": mustselectSize, onClick: () => {
                    //indico all'utente di selezionare la taglia
                    analytics.productActionIntent(ctaData.name, currentColor.productId);
                    setMustselectSize(true);
                    setForceOpenSizeSelector(true);
                    navigation === 'keyboard' &&
                        setTimeout(() => {
                            var _a;
                            const button = (_a = tileRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.selector__menu--fieldset');
                            button === null || button === void 0 ? void 0 : button.focus();
                        }, 200);
                }, customTabIndex: props.disableFocus ? -1 : undefined }, ctaData.text));
        }
        // se isBoutiqueExclusive o notShoppable apro il find in boutique
        if ((product === null || product === void 0 ? void 0 : product.isBoutiqueExclusive) || (product === null || product === void 0 ? void 0 : product.notShoppable)) {
            return (React.createElement(Button, { type: "button", cls: [`button--${buttonStyle}`], onClick: () => {
                    setModalsOpen(true);
                    setShowOverlay(true);
                    analytics.productActionIntent('reserve in store', currentColor.productId);
                } }, t('label.findinboutique')));
        }
        // se la taglia è disponibile e non è coming soon
        if ((currentSize === null || currentSize === void 0 ? void 0 : currentSize.selectable) && !isComingSoon && !product.isPreview) {
            if (props.wishlist && !props.inPublicWishlist) {
                const _wishlistProduct = props.product;
                return (React.createElement(WishlistMoveToCart, { product: _wishlistProduct, type: 'products', size: currentSize, callback: () => props.updateWishlist('remove', { listitemid: _wishlistProduct.listitemid, masterId: _wishlistProduct.masterId, moveToBag: true }), pushAnalytics: () => sendAnalytics('wishlist'), updateErrorMessage: (msg) => setErrorMessage(msg), triggerOpenSize: () => {
                        setMustselectSize(true);
                        setForceOpenSizeSelector(true);
                    } }));
            }
            return (React.createElement(ProductAddToBag, { product: product, size: currentSize, callback: onSuccessAddToBag, pushAnalytics: () => sendAnalytics(analyticsAddToBagDetail), updateErrorMessage: (msg) => setErrorMessage(msg), updateErrorTitle: (msg) => setErrorTitle(msg), hierarchy: buttonStyle }));
        }
        if (props.wishlist) {
            const _Product = props.product;
            return (React.createElement(WishlistActionNotifyMe, { product: _Product, color: currentColor, size: currentSize, from: props.pageType || 'wishlist', triggerOpenSize: () => {
                    setMustselectSize(true);
                    setForceOpenSizeSelector(true);
                } }));
        }
        return (React.createElement(ProductActionNotifyMe, { product: product, color: currentColor, size: currentSize, onOpenModal: () => setModalsOpen(true), onCloseModal: () => setModalsOpen(false), from: props.pageType || 'plp', customClasses: [`button--${buttonStyle}`], triggerOpenSize: () => {
                setMustselectSize(true);
                setForceOpenSizeSelector(true);
            }, excludeForA11N: props.disableFocus }));
    }, [product, currentSize, currentColor, mustselectSize, navigation]);
    const buttonData = useCallback((fallback, name) => {
        var _a, _b;
        if (notMember) {
            return { text: t('button.signintopurchase'), name: 'log in to purchase' };
        }
        else if ((_a = product === null || product === void 0 ? void 0 : product.availability) === null || _a === void 0 ? void 0 : _a.preorder) {
            return { text: t('button.preordernow'), name: 'preorder' };
        }
        else if (!isComingSoon && (product === null || product === void 0 ? void 0 : product.isBoutiqueExclusive)) {
            return { text: t('label.findinboutique'), name: 'reserve in store' };
        }
        else if (((_b = product === null || product === void 0 ? void 0 : product.availability) === null || _b === void 0 ? void 0 : _b.isOutOfStock) || (product === null || product === void 0 ? void 0 : product.isPreview)) {
            return { text: t('label.notifyme'), name: 'notify me' };
        }
        else if (!(product === null || product === void 0 ? void 0 : product.notShoppable) && !(product === null || product === void 0 ? void 0 : product.isBoutiqueExclusive)) {
            return { text: fallback || t('button.addtobag'), name: name || 'quick add' };
        }
        return null;
    }, [product, isComingSoon]);
    const staticQuickButton = useCallback(() => {
        const ctaData = buttonData();
        if (!ctaData)
            return React.createElement(React.Fragment, null);
        if (notMember) {
            return (ctaData && (React.createElement(Button, { type: "button", cls: ['button--tertiary'], className: "product-tile__quite-button", ariaHaspopup: "dialog", onClick: () => {
                    analytics.productActionIntent(ctaData.name, currentColor.productId);
                    setLoginOpen(true);
                }, customTabIndex: props.disableFocus ? -1 : undefined }, ctaData === null || ctaData === void 0 ? void 0 : ctaData.text)));
        }
        return (ctaData && (React.createElement(Button, { type: "button", cls: ['button--tertiary'], className: "product-tile__quite-button", ariaExpanded: false, onClick: () => {
                const analyticsAction = props.inShopTheLookModule ? ' - shop the look main' : null;
                if (props.inShopTheLookModule && props.onShopTheLookClick) {
                    props.onShopTheLookClick();
                }
                else {
                    analytics.productActionIntent(ctaData.name, currentColor.productId, 'click', analyticsAction);
                    setTileState('EXPANDED');
                }
                navigation === 'keyboard' &&
                    setTimeout(() => {
                        var _a;
                        (_a = tileForm.current) === null || _a === void 0 ? void 0 : _a.focus();
                    }, 200);
            }, customTabIndex: props.disableFocus ? -1 : undefined }, ctaData === null || ctaData === void 0 ? void 0 : ctaData.text)));
    }, [navigation, currentColor, notMember, product]);
    /* RENDER FUNCTION */
    const renderShopTheLookPanelInfo = () => {
        return (React.createElement("div", null,
            React.createElement(ProductLink, { className: "shopthelookpanel-link", product: product, analyticsData: analyticsContext === null || analyticsContext === void 0 ? void 0 : analyticsContext.analyticsData }, t('shopthelook.viewproductdetails')),
            tilePrice()));
    };
    const renderProductInfo = () => {
        var _a;
        return (React.createElement("div", { className: classNames('product-tile__info', 'single-slide__labels') },
            React.createElement("div", { className: "product-tile__info_content" },
                React.createElement("div", { className: "product-tile__info_base" },
                    renderProductName(),
                    productSEOName()),
                props.inShopTheLookPanel && renderShopTheLookPanelInfo(),
                !window.unshoppable && (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: classNames('product-tile_quick-buy-wrp', { 'is-show': tileState != 'QUIET' }) }, tileState != 'QUIET' && (React.createElement("section", { ref: tileForm, tabIndex: -1, "aria-label": (isExpanded && t('product.selectsizeandcolor')) || null },
                        React.createElement("div", { className: "product-tile__quick-buy-panel" },
                            React.createElement(ProductSelectionColor, { variationValues: colourVariations, currentId: !props.inShopTheLookModule && !props.inShopTheLookPanel ? currentColor === null || currentColor === void 0 ? void 0 : currentColor.productId : currentColor === null || currentColor === void 0 ? void 0 : currentColor.id, onChangeVariation: (colorVariation) => {
                                    changeColor(colorVariation);
                                }, className: classNames('intile', { 'small-swtches': !props.fullExpanded && !isWideDesktop, inwishlist: !!props.wishlist }), open: isExpanded, hideValue: false, hideLabel: false, groupid: `${Math.random().toString(16).slice(2)}_${props.index}`, showHideLabel: !isExpanded, showSelectedColorName: props.inIconGallery, carousel: useColorCarousel, disableFocus: props.disableFocus, fullExpanded: props.fullExpanded, carouselNavigation: (props.type !== 'HEROCAROUSEL' && isWideDesktop && !props.fullExpanded && 'bottom') || null }),
                            props.inShopTheLookPanel && React.createElement("div", null, quickBuyError()),
                            tileState === 'EXPANDED' && isExpanded && (React.createElement(motion.div, { initial: { y: props.fullExpanded || isReducedMotion ? 0 : 10, opacity: props.fullExpanded || isReducedMotion ? 1 : 0 }, transition: { duration: 0.1 }, animate: { y: 0, opacity: 1 } },
                                React.createElement(ProductSelectionSizesExpanded, { disableFocus: props.disableFocus, variationValues: sizeVariations, current: product, productId: props.product.listitemid, currentId: currentSize === null || currentSize === void 0 ? void 0 : currentSize.id, onChangeVariation: (sizeVariatzion) => {
                                        changeSize(sizeVariatzion);
                                    }, open: forceOpenSizeSelector, className: "intile", selectSizeError: mustselectSize, error: (mustselectSize && t('product.selectsize')) || (((_a = product === null || product === void 0 ? void 0 : product.availability) === null || _a === void 0 ? void 0 : _a.isOutOfStock) && t('label.outofstock')) })))),
                        !props.inShopTheLookPanel && tilePrice(),
                        tileState === 'EXPANDED' &&
                            renderIfShoppable(React.createElement("div", { className: "product-tile__quick-buy" },
                                React.createElement("div", null, !props.inShopTheLookPanel ? quickBuyError() : tilePrice()),
                                React.createElement("div", null, quickBuyButton())))))),
                    tileState == 'QUIET' && tilePrice(),
                    showQuickShop && tileState != 'EXPANDED' && React.createElement("div", { className: "product-tile__quite" }, staticQuickButton()))))));
    };
    const renderProductName = () => {
        const Heading = (props.headingLevel ? props.headingLevel : 'h3');
        return (React.createElement(Heading, { className: 'product-tile__name' },
            React.createElement(ProductLink, { id: `${product.uuid}_link`, isShelf: props.isShelf, noClickTracking: props.noClickTracking, index: props.index, product: product, pageType: props.pageType, className: "product-tile__link", recommender: props === null || props === void 0 ? void 0 : props.recommender, actionList: props.actionList, analyticsData: analyticsContext === null || analyticsContext === void 0 ? void 0 : analyticsContext.analyticsData, tabIndex: props.disableFocus ? -1 : undefined, ignoreRouting: props.pageType != 'PLP' },
                React.createElement("span", { dangerouslySetInnerHTML: { __html: product.productName } }))));
    };
    const renderProductVisual = () => {
        return props.inShopTheLookPanel ? (React.createElement("div", { className: "shopthelookpanel-images" },
            React.createElement(ProductTileVisual, { product: product, tileType: props.type, visual2: '' }),
            React.createElement(ProductTileVisual, { product: product, tileType: props.type, visual1: product.lookDetailAsset || 'R', visual2: '' }))) : props.inShopTheLookModule ? (React.createElement("div", { className: "shopthelookmodule-images" },
            React.createElement("button", { className: "product-tile__link--button", onClick: () => fakeClick(), "aria-hidden": true, tabIndex: -1 },
                React.createElement(ProductTileVisual, { product: product, tileType: props.type, visual1: product.lookDetailAsset || 'F', visual2: '' })))) : (React.createElement("button", { className: "product-tile__link--button", onClick: () => fakeClick(), "aria-hidden": true, tabIndex: -1 },
            React.createElement(ProductTileVisual, { product: product, tileType: props.type, isOver: isOver, customImage: props === null || props === void 0 ? void 0 : props.customImage, isChanged: isChanged === null || isChanged === void 0 ? void 0 : isChanged.current })));
    };
    const renderIfShoppable = (components) => {
        return (!window.unshoppable && !hideCta && components) || React.createElement(React.Fragment, null);
    };
    return (React.createElement("div", { "data-id": `${currentColor ? currentColor.productId : ''}${currentSize ? currentSize.id : ''}`, "data-product": product === null || product === void 0 ? void 0 : product.id, className: classNames(`product-tile single-slide__img product-tile--${tileState}`, {
            'product-tile__wishlist': props.wishlist,
            quickshopactive: tileState === 'EXPANDED',
            fullExpanded: props.fullExpanded,
        }), ref: tileRef, tabIndex: -1 },
        product ? (React.createElement(React.Fragment, null,
            React.createElement(DwMarker, { type: "product", uuid: product.uuid }),
            React.createElement("div", { className: "product-tile__card" },
                React.createElement("div", { className: "product-tile__image-wrapper" },
                    renderProductVisual(),
                    !props.hidelabels && React.createElement(ProductLabels, { limit: 1, product: product, type: "tile" }))),
            renderProductInfo(),
            showWishlistButton &&
                renderIfShoppable(React.createElement("div", { className: "product-tile__wishlist-actions" },
                    props.wishlist && props.wishlistShareEnabled && (React.createElement("button", { "aria-label": t('wishlist.shareitem'), onClick: () => {
                            props.updateWishlist &&
                                props.updateWishlist('share', {
                                    product: product,
                                });
                        }, tabIndex: props.disableFocus ? -1 : undefined },
                        React.createElement(Icon, { family: "16", name: "wishlist-share" }))),
                    (props.wishlist && (React.createElement(WishlistRemoveItem, { color: "black", disableFocus: props.disableFocus, onClick: () => {
                            let _wishlistProduct = props.product;
                            props.updateWishlist('remove', { listitemid: _wishlistProduct.listitemid, masterId: _wishlistProduct.masterId });
                        } }))) || (React.createElement(ProductWishlistToggle, { color: "black", productSize: currentSize, productColor: currentColor, product: product, section: props.type, disableFocus: props.disableFocus, setErrorMessage: setErrorMessage })))))) : (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "product-tile__card" },
                React.createElement("div", { className: "product-tile__link" },
                    React.createElement("div", { className: "product-tile__image-wrapper" },
                        React.createElement("div", { className: "product-image" })))),
            !props.hidelabels && (React.createElement("div", { className: "product-tile__info single-slide__labels" },
                React.createElement("p", { className: "product-tile__name" }, t('label.loading')),
                !props.hideprice && React.createElement("span", { className: "product-tile__price" }, t('label.loading')))))),
        React.createElement(ErrorModal, { label: errorTitle, message: errorMessage, onClose: () => setErrorMessage(undefined) }),
        React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) },
            notMember && React.createElement(LoginModal, { incontext: "membersonly", modalStatus: loginOpen, updateStatus: (status) => setLoginOpen(status) }),
            ((product === null || product === void 0 ? void 0 : product.isBoutiqueExclusive) || (product === null || product === void 0 ? void 0 : product.notShoppable)) && (React.createElement(ProductBoutiqueModal, { isOpen: showOverlay, title: t('label.findinboutique'), mode: "find", product: product, size: currentSize, color: currentColor, colourVariations: colourVariations, sizeVariations: sizeVariations, onRequestClose: () => {
                    setShowOverlay(false);
                } })))));
};
export default ProductTile;
